import { useEffect, useState, type ReactElement } from "react";
import { usePortal } from "../state/PortalContext.js";
import { PageHelp } from "../layout/PageHelp.js";

interface AdminRow {
  adminId: string;
  email: string;
  assignments: Array<{ service: string; stage: string; roles: string[] }>;
}

/** Super-admin page: assign roles to an admin per service + stage. */
export function AdminsPage(): ReactElement {
  const { api, me, catalog } = usePortal();
  const [admins, setAdmins] = useState<AdminRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [adminId, setAdminId] = useState("");
  const [serviceId, setServiceId] = useState(catalog[0]?.serviceId ?? "");
  const [stageId, setStageId] = useState("");
  const [roles, setRoles] = useState<string[]>([]);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!me.superAdmin) return;
    api
      .listAdmins()
      .then(setAdmins)
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load admins"));
  }, [api, me.superAdmin]);

  // Start from the admin's current roles for the chosen service + stage, so saving never drops a
  // role by accident (saving replaces the whole list).
  useEffect(() => {
    const current = admins
      .find((a) => a.adminId === adminId)
      ?.assignments.find((x) => x.service === serviceId && x.stage === stageId)?.roles;
    setRoles(current ?? []);
  }, [adminId, serviceId, stageId, admins]);

  if (!me.superAdmin) return <p>Super-admin access required.</p>;

  const service = catalog.find((s) => s.serviceId === serviceId);
  const roleOptions = service?.roles ?? [];
  const selectedAdmin = admins.find((a) => a.adminId === adminId);

  async function save(): Promise<void> {
    setStatus(null);
    setError(null);
    try {
      await api.setAssignment(adminId, serviceId, stageId, roles);
      const who = selectedAdmin?.email ?? adminId;
      setStatus(
        roles.length > 0
          ? `Saved: ${who} now has ${roles.join(", ")} in ${service?.displayName ?? serviceId} / ${stageId}.`
          : `Saved: ${who} no longer has any role in ${service?.displayName ?? serviceId} / ${stageId}.`
      );
      setAdmins(await api.listAdmins());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    }
  }

  return (
    <section aria-labelledby="admins-title">
      <h1 id="admins-title">Admins</h1>
      <PageHelp>
        <p>Decide what each portal admin is allowed to do. Only super-admins see this screen.</p>
        <ul>
          <li>Roles are given per app and per environment. A role in the test environment gives nothing in the live one.</li>
          <li>An admin with no role in an app does not see that app at all.</li>
          <li>To add someone new, they must first be invited to the portal so they appear in the list below.</li>
        </ul>
      </PageHelp>

      {error ? (
        <p role="alert" className="field-error">
          {error}
        </p>
      ) : null}
      {status ? <p role="status">{status}</p> : null}

      <h2>Current admins</h2>
      <p className="field-hint">Everyone who can sign in to the portal, and the roles they have in each app and environment.</p>
      <table>
        <thead>
          <tr>
            <th scope="col">Email</th>
            <th scope="col">Admin id</th>
            <th scope="col">Roles</th>
          </tr>
        </thead>
        <tbody>
          {admins.map((a) => (
            <tr key={a.adminId}>
              <td>{a.email}</td>
              <td>{a.adminId}</td>
              <td>
                {a.assignments.length === 0
                  ? "None"
                  : a.assignments.map((x) => `${x.service} / ${x.stage}: ${x.roles.join(", ") || "none"}`).join(" | ")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>Change roles</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="field">
          <label htmlFor="a-admin">Admin</label>
          <p id="a-admin-hint" className="field-hint">
            The person whose roles you want to change.
          </p>
          <select id="a-admin" value={adminId} onChange={(e) => setAdminId(e.target.value)} aria-describedby="a-admin-hint">
            <option value="">Select…</option>
            {admins.map((a) => (
              <option key={a.adminId} value={a.adminId}>
                {a.email}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="a-service">App</label>
          <p id="a-service-hint" className="field-hint">
            The app the roles are for.
          </p>
          <select
            id="a-service"
            value={serviceId}
            aria-describedby="a-service-hint"
            onChange={(e) => {
              setServiceId(e.target.value);
              setStageId("");
            }}
          >
            {catalog.map((s) => (
              <option key={s.serviceId} value={s.serviceId}>
                {s.displayName}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="a-stage">Environment</label>
          <p id="a-stage-hint" className="field-hint">
            Production is live with real members; the others are test copies.
          </p>
          <select id="a-stage" value={stageId} onChange={(e) => setStageId(e.target.value)} aria-describedby="a-stage-hint">
            <option value="">Select…</option>
            {(service?.stages ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <fieldset disabled={!adminId || !stageId}>
          <legend>Roles</legend>
          <p className="field-hint">
            Tick every role this admin should have here. The ticked list replaces what they had before; untick all to
            remove their access.
          </p>
          {roleOptions.map((r) => (
            <div key={r.id} className="field">
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={roles.includes(r.id)}
                  aria-describedby={r.description ? `role-hint-${r.id}` : undefined}
                  onChange={(e) => setRoles((prev) => (e.target.checked ? [...prev, r.id] : prev.filter((x) => x !== r.id)))}
                />{" "}
                {r.id}
              </label>
              {r.description ? (
                <p id={`role-hint-${r.id}`} className="field-hint">
                  {r.description}
                </p>
              ) : null}
            </div>
          ))}
        </fieldset>

        <button type="submit" disabled={!adminId || !stageId}>
          Save
        </button>
      </form>
    </section>
  );
}
